import { useEffect } from 'react';
import useGenerateInventoryRequest from '~/hooks/srp/useGenerateInventoryRequest';

/**
 * Manages the merchMaintainScrollPosition sessionStorage flag.
 *
 * On beforeunload (e.g. user clicks a tile to navigate to VDP), the flag is set so that on return
 * (back-button-from-VDP), the SRP can detect it and skip its scroll-to-top behavior — preserving the
 * user's scroll position from before the VDP visit.
 *
 * The scroll-to-top on filter/page change is handled by useSyncUrl, not this hook.
 */

const merchMaintainScrollPosition = 'merchMaintainScrollPosition';

function saveScrollPosition() {
  sessionStorage.setItem(merchMaintainScrollPosition, 'true');
}

export default function useScrollToTop() {
  const { request } = useGenerateInventoryRequest(0);

  useEffect(() => {
    window.addEventListener('beforeunload', saveScrollPosition);

    return () => {
      window.removeEventListener('beforeunload', saveScrollPosition);
    };
  }, []);

  // beforeunload does not fire during SPA navigation. Detect clicks on links
  // that navigate away from SRP so the scroll flag is set before the route changes.
  useEffect(() => {
    const handleNavClick = (event: MouseEvent) => {
      const link = (event.target as Element).closest('a');
      if (!link?.href) return;

      try {
        const url = new URL(link.href);
        if (
          url.origin === window.location.origin &&
          !url.pathname.startsWith('/cars')
        ) {
          saveScrollPosition();
        }
      } catch {
        // ignore non-URL hrefs
      }
    };

    document.addEventListener('click', handleNavClick, { capture: true });

    return () => {
      document.removeEventListener('click', handleNavClick, { capture: true });
    };
  }, []);

  useEffect(() => {
    const shouldMaintainPosition = sessionStorage.getItem(
      merchMaintainScrollPosition
    );
    if (shouldMaintainPosition) {
      sessionStorage.removeItem(merchMaintainScrollPosition);
    }
  }, [request]);
}
